import { io } from "socket.io-client";
import PubSub from "pubsub-js";
import log from "./util/logger";
import {
    ClientSocketEvents,
    ClientState,
    ConnectorEvents,
    Contexts,
    NotificationTypes,
    ServerSocketEvents,
    SocketState,
} from "./constants/constants";

export { SocketState, ClientState, Contexts };

export default class Connector {
    constructor(serviceUrl, serviceId, options) {
        this._serviceId = null;
        this._serviceUrl = null;

        this._serviceUrl = serviceUrl.replace(/\/$/, ""); // remove trailing slash (if there is one)
        if (!this._serviceUrl) {
            throw new Error("serviceUrl not set");
        }
        this._serviceId = serviceId;
        if (!this._serviceId) {
            throw new Error("serviceId not set");
        }

        this._socket = null;
        this._socketId = null;
        this._conArgs = {
            adiaRecon: 0,
            adiaOldId: "none",
            //adiaOldSession: "none",
        };
        this._initialized = false;
        this._isReconnecting = false;
        this._isClosing = false;
        this._serverTime = null;

        this._clientId = "";
        this._clientInfo = {};
        this._clientState = {
            socket: SocketState.NONE,
            client: ClientState.NONE,
        };

        this._productionState = {
            state: null,
            isOpen: null,
            isOnAir: null,
        };
        this._productionInfo = null;
        this._directionInfo = null;
        this._streamInfo = null;
        this._actions = null;

        this._directors = {};
        this._moderators = {};
        this._users = {};
        this._sortedClientLists = { directors: [], moderators: [], users: [] };

        this._commands = [];

        this._options = {
            logLevel: "error",
            enableOnAirTimer: false,
            //notificationEvents: [],
            clientCompareFunction: (a, b) => {
                const aName = a.info.name;
                const bName = b.info.name;
                if (aName > bName) {
                    return 1;
                } else if (aName < bName) {
                    return -1;
                } else {
                    return 0;
                }
            },
            PS: null,
        };

        this._onAirTimer = null;

        if (options) {
            for (let prop in options) {
                if (this._options.hasOwnProperty(prop)) {
                    this._options[prop] = options[prop];
                }
            }
        }

        try {
            log.level = this._options.logLevel;
        } catch {
            log.level = "error";
            log.error(`Invalid log level! Available levels are: ${Object.keys(log.levels)}`);
        }

        if (this._options.PS) {
            this._ps = this._options.PS;
        } else {
            this._ps = PubSub;
        }

        log.debug("options", this._options);
    }

    get serverTime() {
        return this._serverTime;
    }

    get clientId() {
        return this._clientId;
    }

    get clientInfo() {
        return this._clientInfo;
    }

    get clientState() {
        return this._clientState;
    }

    get productionState() {
        return this._productionState;
    }

    get productionInfo() {
        return this._productionInfo;
    }

    get directionInfo() {
        return this._directionInfo;
    }

    get streamInfo() {
        return this._streamInfo;
    }

    get actions() {
        return this._actions;
    }

    _handleSocketError(message) {
        this._ps.publish(ConnectorEvents.SOCKET_ERROR, {
            message,
        });
    }

    _handleServiceError(context, error, timestamp) {
        this._ps.publish(ConnectorEvents.SERVICE_ERROR, {
            context,
            error,
            timestamp,
        });
    }

    _handleActionSuccess(context, timestamp) {
        this._ps.publish(ConnectorEvents.ACTION_SUCCESS, {
            context,
            timestamp,
        });
    }

    _handleSocketStateChange(data, force = false) {
        if (data && (data !== this._clientState.socket || force)) {
            this._clientState.socket = data;
            this._ps.publish(ConnectorEvents.SOCKET_STATE, {
                socketState: this._clientState.socket,
            });
        }
    }

    _handleClientStateChange(data, force = false) {
        if (data && (data !== this._clientState.client || force)) {
            this._clientState.client = data;
            this._ps.publish(ConnectorEvents.CLIENT_STATE, {
                clientState: this._clientState.client,
            });
        }
    }

    _handleNotification(notificationObj, timestamp) {
        const ret = {
            doClientSort: { director: false, moderator: false, user: false },
            clientsChanged: { director: false, moderator: false, user: false },
        };

        const { type, data } = notificationObj;
        switch (type) {
            case NotificationTypes.CLIENT_STATE:
                this._handleSocketStateChange(data.socket);
                this._handleClientStateChange(data.client, true);
                break;

            case NotificationTypes.CLIENT_INFO:
                this._clientInfo = data;
                this._ps.publish(ConnectorEvents.CLIENT_INFO, {
                    clientInfo: this._clientInfo,
                });
                break;

            case NotificationTypes.PRODUCTIONS:
                this._ps.publish(ConnectorEvents.PRODUCTIONS, {
                    productions: data,
                });
                break;

            case NotificationTypes.PRODUCTION_STATE: {
                this._productionState = data;
                this._ps.publish(ConnectorEvents.PRODUCTION_STATE, {
                    productionState: this._productionState,
                    changes: null,
                });
                break;
            }

            case NotificationTypes.PRODUCTION_STATE_CHANGE: {
                for (let prop in data) {
                    this._productionState[prop] = data[prop];
                }
                this._ps.publish(ConnectorEvents.PRODUCTION_STATE, {
                    productionState: this._productionState,
                    changes: data,
                });
                break;
            }

            case NotificationTypes.PRODUCTION_INFO:
                this._productionInfo = data;
                this._ps.publish(ConnectorEvents.PRODUCTION_INFO, {
                    productionInfo: this._productionInfo,
                    changes: null,
                });
                break;

            case NotificationTypes.PRODUCTION_INFO_CHANGE:
                for (let prop in data) {
                    this._productionInfo[prop] = data[prop];
                }
                this._ps.publish(ConnectorEvents.PRODUCTION_INFO, {
                    productionInfo: this._productionInfo,
                    changes: data,
                });
                break;

            case NotificationTypes.DIRECTION_INFO:
                this._directionInfo = data;
                this._ps.publish(ConnectorEvents.DIRECTION_INFO, {
                    directionInfo: this._directionInfo,
                    changes: null,
                });
                break;

            case NotificationTypes.DIRECTION_INFO_CHANGE:
                for (let prop in data) {
                    this._directionInfo[prop] = data[prop];
                }
                this._ps.publish(ConnectorEvents.DIRECTION_INFO, {
                    directionInfo: this._directionInfo,
                    changes: data,
                });
                break;

            case NotificationTypes.STREAM_INFO:
                this._streamInfo = data;
                this._ps.publish(ConnectorEvents.STREAM_INFO, {
                    streamInfo: this._streamInfo,
                    changes: null,
                });

                if (data.isOnAir) {
                    if (this._clientInfo.roles.moderator && this._options.enableOnAirTimer) {
                        this._startOnAirTimer(data.onAirTime, timestamp);
                    }
                } else {
                    if (this._clientInfo.roles.moderator) {
                        this._stopOnAirTimer();
                    }
                }
                break;

            case NotificationTypes.STREAM_INFO_CHANGE:
                for (let prop in data) {
                    this._streamInfo[prop] = data[prop];
                }
                this._ps.publish(ConnectorEvents.STREAM_INFO, {
                    streamInfo: this._streamInfo,
                    changes: data,
                });

                if (data.isOnAir) {
                    if (this._clientInfo.roles.moderator && this._options.enableOnAirTimer) {
                        this._startOnAirTimer(data.onAirTime, timestamp);
                    }
                } else {
                    if (this._clientInfo.roles.moderator) {
                        this._stopOnAirTimer();
                    }
                }
                break;

            case NotificationTypes.CLIENTS:
                this._directors = data.directors;
                this._moderators = data.moderators;
                this._users = data.users;

                ret.doClientSort = {
                    director: true,
                    moderator: true,
                    user: true,
                };
                ret.clientsChanged = {
                    director: true,
                    moderator: true,
                    user: true,
                };
                break;

            case NotificationTypes.CLIENTS_CHANGE:
                for (let i = 0; i < data.length; i++) {
                    const clientChange = data[i];
                    const { id, roles, state } = clientChange.data;

                    let clientList, clientType;
                    if (roles.director) {
                        clientList = this._directors;
                        clientType = "director";
                    } else if (roles.moderator) {
                        clientList = this._moderators;
                        clientType = "moderator";
                    } else {
                        clientList = this._users;
                        clientType = "user";
                    }

                    switch (clientChange.cmd) {
                        case "add":
                            if (!clientList.hasOwnProperty(id)) {
                                clientList[id] = clientChange.data;
                                ret.doClientSort[clientType] = true;
                            }
                            break;

                        case "del":
                            if (clientList.hasOwnProperty(id)) {
                                delete clientList[id];
                                ret.doClientSort[clientType] = true;
                            }
                            break;

                        case "upd":
                            if (clientList.hasOwnProperty(id)) {
                                if (state) {
                                    clientList[id].state = state;
                                    ret.clientsChanged[clientType] = true;
                                }
                            }
                            break;

                        default:
                    }
                }

                ret.clientsChanged.director = ret.doClientSort.director || ret.clientsChanged.director;
                ret.clientsChanged.moderator = ret.doClientSort.moderator || ret.clientsChanged.moderator;
                ret.clientsChanged.user = ret.doClientSort.user || ret.clientsChanged.user;
                break;

            case NotificationTypes.ACTIONS:
                this._actions = data;
                this._ps.publish(ConnectorEvents.ACTIONS, {
                    actions: this._actions,
                });
                break;

            /*case "command":
                this._commands.unshift(data);
                break;
            */
            case NotificationTypes.COMMANDS:
                this._commands = data;
                this._commands.reverse();
                break;

            default:
                this._ps.publish(ConnectorEvents.NOTIFICATION, notificationObj);
        }

        return ret;
    }

    _startOnAirTimer(onAirTime, serverTime) {
        this._stopOnAirTimer();

        let secCount = Math.round((serverTime - onAirTime) / 1000);
        this._ps.publish(ConnectorEvents.ONAIR_TIME_CHANGE, secCount);
        this._onAirTimer = setInterval(() => {
            secCount++;
            this._ps.publish(ConnectorEvents.ONAIR_TIME_CHANGE, secCount);
        }, 1000);
    }

    _stopOnAirTimer() {
        if (this._onAirTimer != null) {
            clearInterval(this._onAirTimer);
            this._onAirTimer = null;
        }
    }

    init() {
        if (this._initialized) {
            return;
        }

        // create socket connecting to service namespace
        const uri = `${this._serviceUrl}/acs/${this._serviceId}`;
        this._socket = io(uri, {
            query: this._conArgs,
            forceNew: true,
            reconnectionDelayMax: 5000,
            reconnectionAttempts: 10,
            transports: ["websocket", "polling"], // websocket first
        });

        let didTransportsSwitch = false;

        this._socket.on("connect_error", () => {
            // revert to classic upgrade
            if (!didTransportsSwitch) {
                this._socket.io.opts.transports = ["polling", "websocket"];
            }
        });

        // default socket events
        this._socket.on("connect", () => {
            this._isReconnecting = false;
            this._socketId = this._socket.id;

            this._conArgs.adiaRecon = 1;
            this._conArgs.adiaOldId = this._socketId;

            log.debug("connect", this._socket.id, this._conArgs);
            this._handleSocketStateChange(SocketState.CONNECTED);
        });

        this._socket.on("disconnect", (reason) => {
            if (this._isClosing) {
                this._isReconnecting = false;
                this._isClosing = false;
            } else {
                log.error("socket disconnect:", reason);
                this._handleSocketError(`disconnect due to ${reason}`);
            }

            if (reason === "io server disconnect" || reason === "io client disconnect") {
                // the disconnection was initiated by the server or the client
                this._handleSocketStateChange(SocketState.DISCONNECTED);
            } else if (!this._isReconnecting) {
                this._isReconnecting = true;
                this._handleSocketStateChange(SocketState.RECONNECTING);
            }
        });

        this._socket.io.on("reconnect_attempt", (number) => {
            if (!didTransportsSwitch) {
                didTransportsSwitch = true;
            }
            log.warn("socket reconnect_attempt", number);
            if (!this._isReconnecting) {
                this._isReconnecting = true;
                this._handleSocketStateChange(SocketState.RECONNECTING);
            }
        });

        this._socket.io.on("reconnect_failed", () => {
            log.error("socket reconnect_failed");

            this._isReconnecting = false;
            this._handleSocketError(`disconnect due to "reconnect_failed"`);
            this._handleSocketStateChange(SocketState.DISCONNECTED);
        });

        this._socket.io.on("error", (err) => {
            if (didTransportsSwitch) {
                log.error("socket error", err.message);
                this._handleSocketError(`error due to ${err.message}`);
            }
        });

        // custom socket events
        this._socket.on(ServerSocketEvents.INITIALIZED, (data, timestamp) => {
            log.debug(ServerSocketEvents.INITIALIZED, data, timestamp);

            this._isReconnecting = false;
            this._serverTime = timestamp;

            this._handleActionSuccess(Contexts.INITIALIZE, timestamp);
            this._handleSocketStateChange(SocketState.CONNECTED);
            this._handleClientStateChange(ClientState.NONE);
        });

        this._socket.on(ServerSocketEvents.RECONNECTED, (data, timestamp) => {
            log.debug(ServerSocketEvents.RECONNECTED, data, timestamp);

            this._isReconnecting = false;
            this._serverTime = timestamp;

            this._handleActionSuccess(Contexts.RECONNECT, timestamp);
            this._handleSocketStateChange(SocketState.CONNECTED);
        });

        this._socket.on(ServerSocketEvents.SERVICE_ERROR, (data, timestamp) => {
            log.error(ServerSocketEvents.SERVICE_ERROR, data, timestamp);
            this._handleServiceError(data.context, data.error, timestamp);
        });

        this._socket.on(ServerSocketEvents.ACTION_SUCCESS, (data, timestamp) => {
            log.debug(ServerSocketEvents.ACTION_SUCCESS, data, timestamp);
            this._handleActionSuccess(data.context, timestamp);
        });

        this._socket.on(ServerSocketEvents.NOTIFICATIONS, (data, timestamp) => {
            log.debug(ServerSocketEvents.NOTIFICATIONS, data, timestamp);

            let doClientSort, clientsChanged;
            data.forEach((notification) => {
                const res = this._handleNotification(notification, timestamp);

                // update doClientSort and clientsChanged flags
                if (
                    notification.type === NotificationTypes.CLIENTS ||
                    notification.type === NotificationTypes.CLIENTS_CHANGE
                ) {
                    if (!doClientSort) doClientSort = {};
                    if (!clientsChanged) clientsChanged = {};

                    doClientSort = Object.keys(res.doClientSort).reduce((acc, curr) => {
                        acc[curr] = doClientSort[curr] || res.doClientSort[curr];
                        return acc;
                    }, {});
                    clientsChanged = Object.keys(res.clientsChanged).reduce((acc, curr) => {
                        acc[curr] = clientsChanged[curr] || res.clientsChanged[curr];
                        return acc;
                    }, {});
                }
            });

            // sort clients if necessary
            if (doClientSort) {
                const sortedClientLists = {};
                if (doClientSort.director) {
                    sortedClientLists.directors = Object.values(this._directors).sort(
                        this._options.clientCompareFunction
                    );
                }
                if (doClientSort.moderator) {
                    sortedClientLists.moderators = Object.values(this._moderators).sort(
                        this._options.clientCompareFunction
                    );
                }
                if (doClientSort.user) {
                    sortedClientLists.users = Object.values(this._users).sort(this._options.clientCompareFunction);
                }
                this._sortedClientLists = {
                    ...this._sortedClientLists,
                    ...sortedClientLists,
                };
            }

            // publish changed clients if necessary
            if (clientsChanged) {
                const hasChangedClientLists = Object.values(clientsChanged).some((b) => !!b);
                if (hasChangedClientLists) {
                    this._ps.publish(ConnectorEvents.CLIENTS, {
                        clients: {
                            directors: this._directors,
                            moderators: this._moderators,
                            users: this._users,
                        },
                        sortedClients: this._sortedClientLists,
                        changedLists: {
                            directors: clientsChanged.director,
                            moderators: clientsChanged.moderator,
                            users: clientsChanged.user,
                        },
                    });
                }
            }
        });

        this._initialized = true;
    }

    connectAsUser(clientId = null, info = {}, modes = {}, metadata = {}) {
        this._socket.emit(ClientSocketEvents.CONNECT_USER, { clientId, info, modes, metadata });
    }

    connectAsModerator(user, token) {
        this._socket.emit(ClientSocketEvents.CONNECT_MODERATOR, { ...user, token });
    }

    connectAsDirector(user, token) {
        this._socket.emit(ClientSocketEvents.CONNECT_DIRECTOR, { ...user, token });
    }

    openProduction(productionId, options) {
        this._socket.emit(ClientSocketEvents.OPEN_PRODUCTION, { productionId, options });
    }

    closeProduction(productionId) {
        this._socket.emit(ClientSocketEvents.CLOSE_PRODUCTION, { productionId });
    }

    connectToProduction(productionId, token) {
        this._socket.emit(ClientSocketEvents.CONNECT_TO_PRODUCTION, { productionId, token });
    }

    disconnectFromProduction() {
        this._socket.emit(ClientSocketEvents.DISCONNECT_FROM_PRODUCTION);
    }

    startBroadcast(token) {
        this._socket.emit(ClientSocketEvents.START_BROADCAST, { token });
    }

    stopBroadcast(token) {
        this._socket.emit(ClientSocketEvents.STOP_BROADCAST, { token });
    }

    setDirectionInfo(changes) {
        this._socket.emit(ClientSocketEvents.SET_DIRECTION_INFO, { ...changes });
    }

    /*addCommand(commands) {
        this._socket.emit("add_command", {commands});
    }*/

    setCam(cam) {
        cam = cam < 1 ? 1 : cam > 3 ? 3 : cam;
        this.setDirectionInfo({ cam });
    }

    setVideoMode(mode) {
        this.setDirectionInfo({ mode });
    }

    setClientState(state) {
        if (this._clientState.socket === SocketState.CONNECTED) {
            this._socket.emit(ClientSocketEvents.SET_CLIENT_STATE, { state: parseInt(state) });
        }
    }

    setClientStateAdd(state) {
        if (this._clientState.socket === SocketState.CONNECTED) {
            this._socket.emit(ClientSocketEvents.SET_CLIENT_STATE, {
                state: this._clientState.client | state,
            });
        }
    }

    setClientStateRemove(state) {
        if (this._clientState.socket === SocketState.CONNECTED) {
            this._socket.emit(ClientSocketEvents.SET_CLIENT_STATE, {
                state: this._clientState.client & ~state,
            });
        }
    }

    close() {
        this._isClosing = true;
        this._socket.close();
    }

    // event handler callback functions
    addOnSocketError(callback) {
        this._ps.subscribe(ConnectorEvents.SOCKET_ERROR, callback);
    }

    addOnServiceError(callback) {
        this._ps.subscribe(ConnectorEvents.SERVICE_ERROR, callback);
    }

    addOnActionSuccess(callback) {
        this._ps.subscribe(ConnectorEvents.ACTION_SUCCESS, callback);
    }

    addOnSocketState(callback) {
        this._ps.subscribe(ConnectorEvents.SOCKET_STATE, callback);
    }

    addOnClientState(callback) {
        this._ps.subscribe(ConnectorEvents.CLIENT_STATE, callback);
    }

    addOnClientInfo(callback) {
        this._ps.subscribe(ConnectorEvents.CLIENT_INFO, callback);
    }

    addOnProductions(callback) {
        this._ps.subscribe(ConnectorEvents.PRODUCTIONS, callback);
    }

    addOnProductionState(callback) {
        this._ps.subscribe(ConnectorEvents.PRODUCTION_STATE, callback);
    }

    addOnProductionInfo(callback) {
        this._ps.subscribe(ConnectorEvents.PRODUCTION_INFO, callback);
    }

    addOnDirectionInfo(callback) {
        this._ps.subscribe(ConnectorEvents.DIRECTION_INFO, callback);
    }

    addOnStreamInfo(callback) {
        this._ps.subscribe(ConnectorEvents.STREAM_INFO, callback);
    }

    addOnActions(callback) {
        this._ps.subscribe(ConnectorEvents.ACTIONS, callback);
    }

    addOnClients(callback) {
        this._ps.subscribe(ConnectorEvents.CLIENTS, callback);
    }

    addOnNotification(callback) {
        this._ps.subscribe(ConnectorEvents.NOTIFICATION, callback);
    }

    addOnCommand(callback) {
        this._ps.subscribe(ConnectorEvents.EXECUTE_COMMAND, callback);
    }

    addOnOnAirTimeChange(callback) {
        this._ps.subscribe(ConnectorEvents.ONAIR_TIME_CHANGE, callback);
    }

    removeCallback(callback) {
        this._ps.unsubscribe(callback);
    }

    removeAllCallbacks() {
        this._ps.unsubscribe(ConnectorEvents.TOP_LEVEL);
    }

    destroy() {
        this.close();

        this._socket = null;
        this._initialized = false;

        this._stopOnAirTimer();
    }
}
